import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Typography, CircularProgress } from '@mui/material';
import './Register.css';
import TurnstileWidget from '../components/TurnstileWidget';
import { AUTH_APP_VERSION, AUTH_LEGAL_NOTICE } from '../constants/authMeta';
import { UserContext } from '../UserContext';
import { clearRegisterSession, isRegisterSessionCurrent } from '../utils/registerSession';

function Register() {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    name: '',
    surname: '',
    email: '',
    company_name: '',
    db_assign_code: '',
  });

  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(Date.now());
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('info');
  const [inviteDetails, setInviteDetails] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [registeredIdentifier, setRegisteredIdentifier] = useState('');
  const [existingAccount, setExistingAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const submitRef = useRef(false);
  const activeRef = useRef(true);
  const [accountToken] = useState(() => localStorage.getItem('token'));
  useEffect(() => { activeRef.current = true; return () => { activeRef.current = false; }; }, []);
  const { setUser } = useContext(UserContext);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberInviteToken = searchParams.get('memberInvite') || '';
  const inviteToken = memberInviteToken || searchParams.get('invite') || '';
  const isMemberInvite = Boolean(memberInviteToken);

  useEffect(() => {
    let cancelled = false;
    setExistingAccount(null);
    const token = accountToken;
    if (!isMemberInvite || !token) { setAccountLoading(false); return undefined; }
    setAccountLoading(true);
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => { if (!cancelled && isRegisterSessionCurrent(token)) setExistingAccount(response.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setAccountLoading(false); });
    return () => { cancelled = true; };
  }, [isMemberInvite, accountToken]);

  const publicEmailDomains = (process.env.REACT_APP_PUBLIC_EMAIL_DOMAINS || '')
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);

  // ✅ Frontend bypass flag (dev/local) — keep consistent with Login
  const TURNSTILE_BYPASS =
    process.env.REACT_APP_TURNSTILE_BYPASS === 'true' ||
    process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (TURNSTILE_BYPASS) {
      setTurnstileToken('bypass-dev-token');
    }
  }, [TURNSTILE_BYPASS]);

  useEffect(() => {
    if (!inviteToken) {
      setInviteDetails(null);
      setInviteError('');
      return;
    }

    let cancelled = false;
    setInviteDetails(null);
    setInviteLoading(true);
    setInviteError('');

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/${isMemberInvite ? 'member-invite' : 'trial-invite'}/${encodeURIComponent(inviteToken)}`)
      .then((response) => {
        if (cancelled) return;

        const invite = response.data;
        setInviteDetails(invite);
        setCredentials((prev) => ({
          ...prev,
          email: invite.email || prev.email,
          company_name: invite.company_name || prev.company_name,
          name: invite.name || prev.name,
          surname: invite.surname || prev.surname,
          db_assign_code: '',
        }));
      })
      .catch((error) => {
        if (cancelled) return;
        setInviteDetails(null);
        setInviteError(
          error.response?.data?.message || 'This invitation is no longer valid.'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setInviteLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inviteToken, isMemberInvite]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitRef.current || (inviteToken && !inviteDetails)) return;

    const emailDomain = credentials.email.split('@')[1]?.toLowerCase();
    if (emailDomain && publicEmailDomains.includes(emailDomain)) {
      setMessageTone('error');
      setMessage('Company email required. Public email domains are not allowed.');
      return;
    }

    if (!TURNSTILE_BYPASS && !turnstileToken) {
      setMessageTone('error');
      setMessage('Security check expired. Please refresh the page or solve the Turnstile again.');
      return;
    }

    submitRef.current = true;
    setIsSubmitting(true);
    setMessageTone('info');
    setMessage('');
    setRegisteredIdentifier('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/register`,
        {
          ...credentials,
          // Only send token if not bypassing
          turnstileToken: TURNSTILE_BYPASS ? undefined : turnstileToken,
          trialInviteToken: inviteDetails && !isMemberInvite ? inviteToken : undefined,
          memberInviteToken: inviteDetails && isMemberInvite ? inviteToken : undefined,
        }
      );

      if (response.status === 201) {
        setMessage(response.data.message || 'Registration successful. Please confirm your email before logging in.');
        if (response.data.emailSent !== false) {
          setMessageTone('success');
          setTimeout(() => navigate('/login'), 2200);
        } else {
          setMessageTone('error');
          setRegisteredIdentifier(credentials.username || credentials.email);
          if (!TURNSTILE_BYPASS) {
            setTurnstileToken('');
            setTurnstileKey(Date.now());
          }
          setIsSubmitting(false);
        }
      } else {
        setMessageTone('error');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error during registration:', err);
      setMessageTone('error');

      if (err.response && err.response.data && err.response.data.message) {
        setMessage(err.response.data.message);
      } else {
        setMessage('Registration failed, please check your inputs and try again.');
      }

      // Force a fresh widget when not bypassing
      if (!TURNSTILE_BYPASS) {
        setTurnstileToken('');
        setTurnstileKey(Date.now());
      }

      setIsSubmitting(false);
    } finally {
      submitRef.current = false;
    }
  };

  const acceptInvitation = async () => {
    if (submitRef.current || !inviteDetails) return;
    if (!isRegisterSessionCurrent(accountToken)) {
      setMessage('Your account changed. Open the current session before accepting this invitation.');
      setMessageTone('error');
      return;
    }
    submitRef.current = true;
    setIsSubmitting(true); setMessage('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/member-invite/${encodeURIComponent(memberInviteToken)}/accept`, {},
        { headers: { Authorization: `Bearer ${accountToken}` } });
      if (!activeRef.current || !isRegisterSessionCurrent(accountToken)) return;
      // Navigate before session removal remounts the application shell.
      navigate('/login', { replace: true, state: { message: response.data.message } });
      clearRegisterSession();
      setUser(null);
    } catch (error) {
      if (!activeRef.current || !isRegisterSessionCurrent(accountToken)) return;
      setMessage(error.response?.data?.message || 'Could not join this register.'); setMessageTone('error');
      setIsSubmitting(false);
    } finally { submitRef.current = false; }
  };

  const handleResendConfirmation = async () => {
    if (!registeredIdentifier || isResendingConfirmation) return;

    setIsResendingConfirmation(true);
    setMessageTone('info');
    setMessage('Requesting a new confirmation email...');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/resend-confirmation`,
        {
          identifier: registeredIdentifier,
          turnstileToken: TURNSTILE_BYPASS ? undefined : turnstileToken,
        }
      );
      setMessageTone('info');
      setMessage(
        response.data?.message ||
          'If the account is still awaiting verification, a new confirmation link will be sent shortly.'
      );
    } catch (error) {
      console.error('Error requesting confirmation email:', error);
      setMessageTone('error');
      setMessage('We could not submit the request. Please check your connection and try again.');
    } finally {
      setIsResendingConfirmation(false);
      if (!TURNSTILE_BYPASS) {
        setTurnstileToken('');
        setTurnstileKey(Date.now());
      }
    }
  };

  const formBusy = inviteLoading || accountLoading || isSubmitting || isResendingConfirmation;
  const invitationUnavailable = Boolean(inviteToken && (!inviteDetails || inviteError));

  return (
    <div className="login-container">
      <div className="login-shell">
        <div className="login-left">
          <div className="login-left-inner">
            <img src="/logo_white.png" alt="EXREG" className="login-brand-logo" />
            <Typography className="login-brand-subtitle">
              Hazardous Area Equipment Register & Inspection Platform
            </Typography>

            <ul className="login-highlights">
              <li>Fast onboarding with register assignment code</li>
              <li>Role-based access and secure cloud registers</li>
              <li>Offline inspections + evidence photo sync</li>
            </ul>

            <div className="login-left-badges">
              <span className="login-badge">Secure</span>
              <span className="login-badge">Audit-ready</span>
              <span className="login-badge">Enterprise</span>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="login-box">
            <Typography className="login-title">{isMemberInvite ? 'Join your register' : 'Create account'}</Typography>
            <Typography className="login-subtitle">
              {inviteDetails
                ? isMemberInvite ? `You are invited to ${inviteDetails.registerName} as ${inviteDetails.role}.` : 'Your trial workspace is ready. Complete your account to continue.'
                : 'Use your company email. If you have a register code, add it to join instantly.'}
            </Typography>

            {inviteLoading && (
              <Typography variant="body2" className="login-message info" role="status">
                Loading your invitation...
              </Typography>
            )}

            {inviteError && (
              <Typography variant="body2" className="login-message error">
                {inviteError}
              </Typography>
            )}

            {isMemberInvite && existingAccount ? <div className="login-form">
              <Typography variant="body2">Signed in as {existingAccount.email || existingAccount.username}.</Typography>
              <Button variant="contained" fullWidth disabled={formBusy || invitationUnavailable} onClick={acceptInvitation}>{isSubmitting ? 'Joining…' : 'Accept invitation and join'}</Button>
              <Button fullWidth disabled={formBusy} onClick={() => { navigate(`/login?memberInvite=${encodeURIComponent(memberInviteToken)}`, { replace: true }); clearRegisterSession(); setUser(null); }}>Sign in with another account</Button>
            </div> : <form onSubmit={handleSubmit} className="login-form" aria-busy={isSubmitting}>
              <TextField
                label="Username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                margin="normal"
                autoComplete="username"
                disabled={formBusy}
              />

              <TextField
                label="Password"
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                margin="normal"
                autoComplete="new-password"
                disabled={formBusy}
              />

              <div className="register-grid">
                <TextField
                  label="First Name"
                  name="name"
                  value={credentials.name}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  autoComplete="given-name"
                  disabled={formBusy}
                />
                <TextField
                  label="Last Name"
                  name="surname"
                  value={credentials.surname}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  autoComplete="family-name"
                  disabled={formBusy}
                />
              </div>

              <TextField
                label="Company Name"
                name="company_name"
                value={credentials.company_name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                margin="normal"
                autoComplete="organization"
                InputProps={{ readOnly: !!inviteDetails }}
                disabled={formBusy}
              />

              <TextField
                label="Company Email"
                name="email"
                type="email"
                value={credentials.email}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                margin="normal"
                autoComplete="email"
                InputProps={{ readOnly: !!inviteDetails }}
                disabled={formBusy}
              />

              {!inviteToken && (
                <TextField
                  label="Register Code (Optional)"
                  name="db_assign_code"
                  value={credentials.db_assign_code || ''}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  helperText="Provided by your administrator"
                  disabled={formBusy}
                />
              )}

              {!TURNSTILE_BYPASS && (
                <div className="login-turnstile">
                  <TurnstileWidget key={turnstileKey} onVerify={setTurnstileToken} />
                </div>
              )}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className="login-primary-btn"
                disabled={formBusy || invitationUnavailable || (!TURNSTILE_BYPASS && !turnstileToken)}
              >
                {isSubmitting ? (
                  <span className="login-button-loading">
                    <CircularProgress size={18} thickness={5} className="login-button-spinner" />
                    <span>Creating account...</span>
                  </span>
                ) : (
                  'Create account'
                )}
              </Button>

              <Button
                variant="text"
                color="secondary"
                fullWidth
                className="login-forgot-btn"
                onClick={() => navigate(isMemberInvite ? `/login?memberInvite=${encodeURIComponent(memberInviteToken)}` : '/login')}
                disabled={formBusy}
              >
                {isMemberInvite ? 'Already have an account? Sign in' : 'Back to login'}
              </Button>
            </form>}

            {message && (
              <Typography
                variant="body2"
                className={`login-message ${messageTone}`}
              >
                {message}
              </Typography>
            )}

            {registeredIdentifier && (
              <Button
                type="button"
                variant="text"
                color="primary"
                fullWidth
                className="login-forgot-btn"
                onClick={handleResendConfirmation}
                disabled={
                  isResendingConfirmation ||
                  (!TURNSTILE_BYPASS && !turnstileToken)
                }
              >
                {isResendingConfirmation ? 'Requesting email...' : 'Resend confirmation email'}
              </Button>
            )}

            <div
              className="login-meta"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
            >
              <Typography variant="caption" className="login-version" style={{ textAlign: 'center' }}>
                {AUTH_APP_VERSION}
              </Typography>
              <Typography variant="caption" className="login-legal" style={{ textAlign: 'center' }}>
                {AUTH_LEGAL_NOTICE}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
